import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList } from 'recharts';
import styles from './NetSalesAddonsBarChart.module.scss';
import { Spinner } from 'components/UI';
import type { AddonsInsightsReport } from 'model/AddonsInsightsReport';

type AddonPoint = {
    id: string;
    addonName: string;
    category: string;
    value: number;
    percent: number;
};

const ADDON_BAR_COLOR = '#BAA997';

interface AddonsProps {
    report: AddonsInsightsReport;
    loading: boolean;
}

const formatMoney = (n: number) =>
    n.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const ellipsis = (s: string, max: number) => {
    if (!s) return '—';
    if (max <= 3) return s.slice(0, Math.max(0, max - 1)) + '…';
    return s.length > max ? s.slice(0, max - 1) + '…' : s;
};

export const NetSalesAddonsBarChart: React.FC<AddonsProps> = ({ report, loading }) => {
    const data: AddonPoint[] = React.useMemo(() => {
        const rows = report?.addons ?? [];
        return rows.map((r, idx) => ({
            id: String(r.addonServiceId ?? r.addonName ?? idx),
            addonName: r.addonName ?? '—',
            category: r.category ?? '—',
            value: Number(r.netSales ?? 0),
            percent: Number((Number(r.netAddonsSalesPct ?? 0) * 100).toFixed(0))
        }));
    }, [report]);

    const wrapRef = React.useRef<HTMLDivElement | null>(null);
    const [wrapWidth, setWrapWidth] = React.useState(0);

    React.useEffect(() => {
        const el = wrapRef.current;
        if (!el) return;

        const update = () => setWrapWidth(el.getBoundingClientRect().width || 0);
        update();

        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const tickBudget = React.useMemo(() => {
        const count = Math.max(data.length, 1);
        const usable = Math.max(wrapWidth - (24 + 24) - 40, 0);
        const perTickPx = usable / count;

        const SAFE = 0.7;
        const effectivePx = perTickPx * SAFE;

        const avgCharPx = 6.8;
        const maxCharsLine1 = Math.max(Math.floor((effectivePx - 10) / avgCharPx), 3);
        const maxCharsLine2 = Math.max(Math.floor((effectivePx - 10) / avgCharPx), 3);

        return { maxCharsLine1, maxCharsLine2 };
    }, [wrapWidth, data.length]);

    const ValuePercentLabel = React.useCallback(
        ({ x, y, width, value, index }: any) => {
            const cx = (x ?? 0) + (width ?? 0) / 2;
            const p = data[index]?.percent ?? 0;

            return (
                <g>
                    <text
                        x={cx}
                        y={(y ?? 0) - 20}
                        textAnchor="middle"
                        fill="#3a3a3a"
                        fontWeight="600"
                    >
                        {formatMoney(Number(value ?? 0))}
                    </text>

                    <text
                        x={cx}
                        y={(y ?? 0) - 5}
                        textAnchor="middle"
                        fill="#9b9b9b"
                        fontSize="12"
                    >
                        {p}%
                    </text>
                </g>
            );
        },
        [data]
    );

    const AddonTick = React.useCallback(
        (props: any) => {
            const { x = 0, y = 0, payload } = props ?? {};
            const addonName = payload?.value as string | undefined;
            if (!addonName) return <g />;

            const item = data.find(d => d.addonName === addonName);
            const category = item?.category ?? '—';

            const line1 = ellipsis(addonName, tickBudget.maxCharsLine1);
            const line2 = ellipsis(`(${category})`, tickBudget.maxCharsLine2);

            return (
                <g transform={`translate(${x},${y})`}>
                    <title>{`${addonName}\n(${category})`}</title>

                    <text
                        dy={16}
                        textAnchor="middle"
                        fill="#2b2b2b"
                        fontWeight="700"
                    >
                        {line1}
                    </text>

                    <text
                        dy={34}
                        textAnchor="middle"
                        fill="#9b9b9b"
                        fontSize="12"
                    >
                        {line2}
                    </text>
                </g>
            );
        },
        [data, tickBudget.maxCharsLine1, tickBudget.maxCharsLine2]
    );

    return (
        <div className={styles.chartCard}>
            <div
                className={styles.barWrap}
                ref={wrapRef}
            >
                {loading ? (
                    <div className={styles.loadingCell}>
                        <Spinner className={styles.spinner} />
                    </div>
                ) : (
                    <ResponsiveContainer
                        width="100%"
                        height={420}
                    >
                        <BarChart
                            data={data}
                            margin={{ top: 60, right: 24, left: 24, bottom: 70 }}
                        >
                            <CartesianGrid
                                stroke="#eee"
                                vertical={false}
                            />

                            <XAxis
                                dataKey="addonName"
                                tick={AddonTick}
                                axisLine={false}
                                tickLine={false}
                                interval={0}
                                minTickGap={0}
                                height={60}
                                label={{
                                    value: 'Add-Ons',
                                    position: 'insideBottom',
                                    offset: -50,
                                    style: { fill: '#7A7A7A', fontWeight: 400, fontSize: '15px' }
                                }}
                            />

                            <YAxis
                                axisLine={false}
                                tickLine={false}
                                width={70}
                                tick={{ fill: '#9b9b9b' }}
                                tickFormatter={(v: number) => {
                                    const n = Number(v ?? 0);
                                    if (n >= 1000) return `$${Math.round(n / 1000)}k`;
                                    return `$${Math.round(n)}`;
                                }}
                                domain={[0, (max: number) => Math.ceil(max * 1.1)]}
                                label={{
                                    value: 'Net Sales Add-Ons ($)',
                                    angle: -90,
                                    position: 'insideLeft',
                                    style: {
                                        textAnchor: 'middle',
                                        fill: '#7A7A7A',
                                        fontWeight: 400,
                                        fontSize: '15px'
                                    }
                                }}
                            />

                            <Bar
                                dataKey="value"
                                fill={ADDON_BAR_COLOR}
                                radius={[8, 8, 0, 0]}
                                maxBarSize={64}
                            >
                                <LabelList
                                    dataKey="value"
                                    content={ValuePercentLabel}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                )}
            </div>
        </div>
    );
};

export default NetSalesAddonsBarChart;
